import React, { useMemo } from 'react'
import { useRegistrationFlow } from 'flows/registration/useRegistrationFlow'
import { FlowRenderer } from 'flows/common/components/FlowRenderer'
import { onboardingSteps } from 'flows/onboarding/steps'
import { SiderImagePreloader, StepImagePreloader } from './ImagePreloader'
import { useTrackRegistrationSteps } from 'hooks/useTrackRegistrationSteps'
import { PreviewProvider } from 'screens/VirtualMarketerOnboarding/PreviewProvider/PreviewProvider'
import { observer } from 'mobx-react-lite'
import PersistedPreview from 'screens/VirtualMarketerOnboarding/Preview/PersistedPreview'
import Styles from './RegistrationAndOnboarding.module.scss'
import { BASE_ASSET_URL } from 'flows/common/constants'
import { step, defineSteps } from 'flows/common/helpers/defineSteps'
import { BusinessType } from 'flows/common/steps/BusinessType/BusinessType'
import { CreateAccount } from 'flows/common/steps/CreateAccount/CreateAccount'
import { VerifyEmail } from 'flows/common/steps/VerifyEmail/VerifyEmail'
import Registration from 'flows/common/steps/Registration/Registration'
import { useSession } from 'hooks/useSession'
import { Button } from 'blaze-ui/Button'
import cx from 'classnames'
import { HOME_PATH, LOGIN_PATH } from 'paths'
import { generatePath, Redirect, useHistory } from 'react-router-dom'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { ALMANAC_CORE_WORKSPACE_ID } from 'utils/constants'
import { useRootStore } from 'stores/RootStore'
import { Spinner } from 'common/Spinner/Spinner'
import { useFetchWorkspaces } from 'hooks/useFetchWorkspaces'
import { Logout1 } from 'blaze-ui/icons'
import { BlazeLogo } from 'flows/common/steps/Registration/BlazeLogo/BlazeLogo'

export const vmRegistrationSteps = defineSteps([
  step('registration', Registration).withImage(`${BASE_ASSET_URL}/1.jpeg`),
  step('verifyEmail', VerifyEmail).hideBackButton().withImage(`${BASE_ASSET_URL}/1.jpeg`),
  step('createAccount', CreateAccount).withImage(`${BASE_ASSET_URL}/2.jpeg`),
  step('businessType', BusinessType).hideBackButton().withImage(`${BASE_ASSET_URL}/3.jpeg`),
])

export const RegistrationAndOnboarding: React.FC = observer(() => {
  const { current, send, isLoading: flowLoading, isDone, resetFlow } = useRegistrationFlow()
  useTrackRegistrationSteps(current)
  const { auth, logout } = useSession()
  const history = useHistory()
  const currentWorkspace = useCurrentWorkspace()
  const allSteps = useMemo(() => [...vmRegistrationSteps, ...onboardingSteps], [])

  useFetchWorkspaces()

  const progressPercentage = useMemo(() => {
    if (flowLoading || isDone) return 0
    const stateId = typeof current.value === 'string' ? current.value : Object.keys(current.value)[0]

    const currentStepIndex = allSteps.findIndex((s) => s.id === stateId)
    const visibleStep = currentStepIndex === 0 ? 0 : currentStepIndex + 1

    return (visibleStep / allSteps.length) * 100
  }, [flowLoading, isDone, current.value, allSteps])

  const currentStep = useMemo(() => {
    return allSteps.find((s) => s.id === current.value)
  }, [current.value, allSteps])

  const handleLogout = async () => {
    logout({ logoutRedirect: null })

    await resetFlow()

    history.replace(LOGIN_PATH)
  }

  const isRegistrationStep = ['registration', 'verifyEmail', 'createAccount'].includes(current.value as string)

  if (currentWorkspace && currentWorkspace.id !== ALMANAC_CORE_WORKSPACE_ID && !currentWorkspace?.is_virtual_marketer) {
    return <Redirect to={generatePath(HOME_PATH)} />
  }

  return (
    <PreviewProvider>
      <div className={Styles.root}>
        {!isRegistrationStep && auth && (
          <div className={cx(Styles.logout, currentStep?.image && Styles.withSiderImage)}>
            <Button variant="editor" onPress={handleLogout} frontIcon={Logout1}>
              Sign Out
            </Button>
          </div>
        )}
        {!isRegistrationStep && <BlazeLogo />}

        {current.value === 'enhanceBrandColors' && (
          <div className={Styles.previewContainer}>
            <PersistedPreview showShuffleButton />
          </div>
        )}
        <FlowRenderer
          current={current}
          send={send}
          steps={allSteps}
          ctx={current.context}
          isLoading={flowLoading}
          isDone={isDone}
          progressPercentage={progressPercentage}
        />
        <StepImagePreloader />
        <SiderImagePreloader />
      </div>
    </PreviewProvider>
  )
})

const RegistrationAndOnboardingWrapper = observer(() => {
  const { featureFlags } = useRootStore()
  if (!featureFlags.ready) {
    return <Spinner />
  }
  return <RegistrationAndOnboarding />
})

export default RegistrationAndOnboardingWrapper
